/**
 * Shared constants for map components
 */

import { MapLayerSettings } from '../../../../shared/types/map.types';

/**
 * Color palette for View By modes (Site, Bid Item, Day, Inspector)
 * Used consistently across all map components for legend colors
 */
export const MAP_VIEW_BY_COLORS = [
  '#6583FF', // Violet Blue
  '#4BA591', // Teal
  '#D86A99', // Pink
  '#0091FF', // Sky Blue
  '#DBBA60', // Gold
  '#3D57C5', // Deep Blue
  '#00D9A8', // Mint
  '#00C5D3', // Cyan
  '#A08438', // Olive
];

/**
 * Layer visibility defaults for different map modes
 * Centralized configuration for consistent behavior across all map components
 *
 * Note on data sources:
 * - siteBoundary: Comes from project.site.geometry (always available per site)
 * - asBuilt: Comes from daily report bid item geometries (report_bid_item.sources.source_geometry)
 * - picture/bidItem/activity/delivery: Marker types from daily reports
 */
export const LAYER_DEFAULTS: {
  /** Default layers for non-fullscreen mode (site overview) */
  nonFullscreen: MapLayerSettings;
  /** Default layers for fullscreen mode (multi-day view) */
  fullscreen: MapLayerSettings;
  /** Default layers for single-day view (all layers enabled) */
  singleDay: MapLayerSettings;
  /** Default layers for drilldown mode (bid item, activity, delivery detail) */
  drilldown: MapLayerSettings;
} = {
  // Non-fullscreen mode: Show only site boundaries (from project.site.geometry)
  nonFullscreen: {
    siteBoundary: true,
    picture: false,
    asBuilt: false,
    bidItem: false,
    activity: false,
    delivery: false,
    forms: false,
    warrantyClaim: false,
  },

  // Fullscreen mode (multi-day): Show site boundaries and as-builts
  fullscreen: {
    siteBoundary: true,
    picture: false,
    asBuilt: true,
    bidItem: false,
    activity: false,
    delivery: false,
    forms: false,
    warrantyClaim: false,
  },

  // Single-day view: ALL layers and types enabled (both FS and non-FS modes)
  singleDay: {
    siteBoundary: true,
    picture: true,
    asBuilt: true,
    bidItem: true,
    activity: true,
    delivery: true,
    forms: true,
    warrantyClaim: false,
  },

  // Drilldown mode: Show site boundaries and as-builts for the selected item
  drilldown: {
    siteBoundary: true,
    picture: false,
    asBuilt: true,
    bidItem: false,
    activity: false,
    delivery: false,
    forms: false,
    warrantyClaim: false,
  },
};

// Warranty claim marker colors — Figma palette only (no yellow, purple, or red).
// Review group uses Blue 2 (#6583FF); in_progress uses Primary Blue (#1036D6) so
// it reads distinctly. Sent-back / rejected / disputed use 2nd-Pink (#EF5497).
export const WARRANTY_CLAIM_STATUS_COLORS: Record<string, string> = {
  draft: '#60698B',
  submitted: '#6583FF',
  sent_back: '#EF5497',
  sent_to_contractor: '#6583FF',
  contractor_notified: '#6583FF',
  responded: '#6583FF',
  in_progress: '#1036D6',
  pm_review: '#6583FF',
  verified: '#36AB19',
  approved: '#36AB19',
  remediation_complete: '#36AB19',
  closed: '#36AB19',
  rejected: '#EF5497',
  disputed: '#EF5497',
};

/**
 * Helper to get a copy of layer defaults (prevents mutation)
 */
export function getLayerDefaults(mode: keyof typeof LAYER_DEFAULTS): MapLayerSettings {
  return { ...LAYER_DEFAULTS[mode] };
}
